import moment from 'moment';

import {Session} from 'nutshell-core/session';

import type {
    UserQuotaFragment as User,
    UserQuotaFragment_quotas as Quota,
} from 'nutshell-graphql-types';

import {
    formatCurrencyDisplay,
    getCurrentFiscalYear,
    getFiscalYearEndMonth,
    getFiscalYearStartMonth,
} from './helpers';
import {QuotaHeaderValue} from './quota-header-value';

type Props = {
    user: User;
    existingQuota: Quota;
    formattedYearTotal: string;
};

export function QuotaHeaderOverview(props: Props) {
    const isCurrentYear = getCurrentFiscalYear() === props.existingQuota?.fiscalYear;

    if (!isCurrentYear && props.formattedYearTotal) {
        return (
            <QuotaHeaderValue
                label={`${props.existingQuota.fiscalYear} total`}
                value={props.formattedYearTotal}
            />
        );
    }

    const currentMonth = moment().month() + 1;
    const thisMonthValue = getMonthValue(props.existingQuota, currentMonth);
    const nextMonthValue = getNextMonthValue(
        currentMonth,
        getCurrentFiscalYear(),
        props.existingQuota,
        props.user
    );

    return (
        <div className='flex align-center gap-16'>
            {thisMonthValue && <QuotaHeaderValue label='This month' value={thisMonthValue} />}
            {nextMonthValue && <QuotaHeaderValue label='Next month' value={nextMonthValue} />}
        </div>
    );
}

function getNextMonthValue(
    currentMonth: number,
    currentFiscalYear: number,
    existingQuota: Quota,
    user: User
): string | undefined {
    if (currentMonth === getFiscalYearEndMonth()) {
        const nextFiscalYear = currentFiscalYear + 1;
        const nextFiscalYearQuota = user.quotas.find(
            (quota) => quota.fiscalYear === nextFiscalYear
        );
        if (nextFiscalYearQuota) {
            return getMonthValue(nextFiscalYearQuota, getFiscalYearStartMonth());
        }
        return undefined;
    }

    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    return getMonthValue(existingQuota, nextMonth);
}

function getMonthValue(quota: Quota, month: number): string | undefined {
    const currencyCode = Session.getInstanceDefaultCurrencyCode();

    const amount = quota.months.find((m) => m.identifier === month)?.value.amount;
    if (!amount) {
        return undefined;
    }

    return formatCurrencyDisplay(amount, currencyCode);
}
